import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { StoryResponseDto } from '../dto/response/story-response.dto';
import storyService from '../services/story.service';
import { StorySaveRequestDto } from '../dto/request/story-save-request.dto';
import { StoryUpdateRequestDto } from '../dto/request/story-update-request.dto';
import { showError, showSuccess } from '../../../../utils/Notifications';
import { AxiosError } from 'axios';

const initialState = {
  status: 'idle',
  stories: [] as StoryResponseDto[] | null,
  filteredStories: [] as StoryResponseDto[] | null,
  story: {} as StoryResponseDto | null,
  error: null as string | null,
};

export const fetchStories = createAsyncThunk(
  'stories/fetchStories',
  async () => {
    return await storyService.fetchStories();
  }
);

export const fetchStoryById = createAsyncThunk(
  'stories/fetchStoryById',
  async (id: string) => {
    return await storyService.fetchStoryById(id);
  }
);

export const fetchStoriesByCompactId = createAsyncThunk(
  'stories/fetchStoriesByCompactId',
  async (compactId: string) => {
    return await storyService.fetchStoriesByCompactId(compactId);
  }
);

// export const fetchStoriesByProjectId = createAsyncThunk(
//   'stories/fetchStoriesByProjectId',
//   async (projectId: string) => {
//     return await storyService.fetchStoriesByProjectId(projectId);
//   }
// );

export const saveCompactStory = createAsyncThunk(
  'stories/saveCompactStory',
  async (
    params: { saveRequest: StorySaveRequestDto; id: string },
    { rejectWithValue }
  ) => {
    const { saveRequest, id } = params;
    try {
      return await storyService.saveStory(saveRequest, 'CompactStory', id);
    } catch (e) {
      return rejectWithValue((e as AxiosError).response?.data ?? 'Failed');
    }
  }
);

// export const saveProjectStory = createAsyncThunk(
//   'stories/saveProjectStory',
//   async (
//     params: { saveRequest: StorySaveRequestDto; id: string },
//     { rejectWithValue }
//   ) => {
//     const { saveRequest, id } = params;
//     try {
//       return await storyService.saveStory(saveRequest, 'ProjectStory', id);
//     } catch (e) {
//       return rejectWithValue((e as AxiosError).response?.data ?? 'Failed');
//     }
//   }
// );

export const updateStory = createAsyncThunk(
  'stories/updateStory',
  async (updateRequest: StoryUpdateRequestDto, { rejectWithValue }) => {
    try {
      return await storyService.updateStory(updateRequest);
    } catch (e) {
      return rejectWithValue((e as AxiosError).response?.data ?? 'Failed');
    }
  }
);

export const deleteStory = createAsyncThunk(
  'stories/deleteStory',
  async (
    payload: { existingId: string; compactId: string | null },
    { rejectWithValue, dispatch }
  ) => {
    try {
      await storyService.deleteStory(payload.existingId, payload.compactId);
      dispatch(fetchStories());
    } catch (e) {
      return rejectWithValue((e as AxiosError).response?.data ?? 'Failed');
    }
  }
);

const storySlice = createSlice({
  name: 'storySlice',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchStories.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchStories.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.stories = action.payload;
      })
      .addCase(fetchStories.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(fetchStoryById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchStoryById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.story = action.payload;
      })
      .addCase(fetchStoryById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(fetchStoriesByCompactId.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchStoriesByCompactId.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.filteredStories = action.payload;
      })
      .addCase(fetchStoriesByCompactId.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      // .addCase(fetchStoriesByProjectId.pending, (state) => {
      //   state.status = 'loading';
      // })
      // .addCase(fetchStoriesByProjectId.fulfilled, (state, action) => {
      //   state.status = 'succeeded';
      //   state.filteredStories = action.payload;
      // })
      // .addCase(fetchStoriesByProjectId.rejected, (state, action) => {
      //   state.status = 'failed';
      //   state.error = action.error?.message ?? null;
      // })
      .addCase(saveCompactStory.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(saveCompactStory.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.stories?.push(action.payload);
        showSuccess('Story added successfully.');
      })
      .addCase(saveCompactStory.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
        showError(`Saving story failed.${action?.payload as string}`);
      })
      // .addCase(saveProjectStory.pending, (state) => {
      //   state.status = 'loading';
      // })
      // .addCase(saveProjectStory.fulfilled, (state, action) => {
      //   state.status = 'succeeded';
      //   state.stories?.push(action.payload);
      //   showSuccess('Story added successfully.');
      // })
      // .addCase(saveProjectStory.rejected, (state, action) => {
      //   state.status = 'failed';
      //   state.error = action.error?.message ?? null;
      //   showError(`Saving story failed.${action?.payload as string}`);
      // })
      .addCase(updateStory.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateStory.fulfilled, (state, action) => {
        state.status = 'succeeded';
        showSuccess('Story saved successfully.');
        if (state.stories) {
          const storyIndex = state.stories.findIndex(
            (story) => story.id === action.payload.id
          );
          if (storyIndex !== -1) {
            state.stories[storyIndex] = action.payload;
          }
        }
      })
      .addCase(updateStory.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
        showError(`Updating story failed.${action?.payload as string}`);
      })
      .addCase(deleteStory.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteStory.fulfilled, (state) => {
        state.status = 'succeeded';
        showSuccess('Delete success!');
      })
      .addCase(deleteStory.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
        showError(`Deleting story failed.${action?.payload as string}`);
      });
  },
});

export default storySlice.reducer;
